import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtEUR, fmtTRY } from "../lib/api";
import { Image as ImageIcon, TrendingUp, Users, RefreshCw, Anchor, ListChecks } from "lucide-react";
import PassengersModal from "../components/PassengersModal";
import WeatherCard from "../components/WeatherCard";
import { itemTotal } from "./Shopping";
import { computeFullSettlement } from "../lib/settlement";

export default function Dashboard() {
  const navigate = useNavigate();
  const [cabins, setCabins] = useState([]);
  const [payments, setPayments] = useState([]);
  const [shopping, setShopping] = useState([]);
  const [actuals, setActuals] = useState([]);
  const [settings, setSettings] = useState({ eur_try_rate: 44, rate_source: "", rate_updated_at: null });
  const [shipImg, setShipImg] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showPax, setShowPax] = useState(false);
  const [muhasebe, setMuhasebe] = useState(null);
  const [realizationShopPct, setRealizationShopPct] = useState(100);
  const [realizationDigerPct, setRealizationDigerPct] = useState(100);
  const [digerPerCabinPaid, setDigerPerCabinPaid] = useState({}); // { cabin_id: paid_try }
  const [shopPerCabinPaid, setShopPerCabinPaid] = useState({});   // { cabin_id: paid_try }
  const [digerSummary, setDigerSummary] = useState({ target_try: 0, paid_try: 0 });
  const [shopPaidTotal, setShopPaidTotal] = useState(0); // Muhasebe > Alışveriş ödemeleri toplamı

  const load = async () => {
    try {
      const [c, p, sh, ac, st, shp, mu, dpc, dsum] = await Promise.all([
        api.get("/cabins"), api.get("/payments"), api.get("/shopping"), api.get("/actuals"), api.get("/settings"), api.get("/ship"), api.get("/muhasebe/balance").catch(() => ({ data: null })), api.get("/diger-maliyet-per-cabin").catch(() => ({ data: null })), api.get("/diger-maliyet-summary").catch(() => ({ data: null })),
      ]);
      setCabins(Array.isArray(c.data) ? c.data : []);
      setPayments(Array.isArray(p.data) ? p.data : []);
      setShopping(Array.isArray(sh.data) ? sh.data : []);
      setActuals(Array.isArray(ac.data) ? ac.data : []);
      setSettings(st.data && typeof st.data === "object" ? st.data : { eur_try_rate: 44 });
      const parsePct = (v) => {
        if (v === undefined || v === null || v === "") return 100;
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 100;
      };
      setRealizationShopPct(parsePct(st.data?.realization_shop_pct ?? st.data?.realization_pct));
      setRealizationDigerPct(parsePct(st.data?.realization_diger_pct ?? st.data?.realization_pct));
      setShipImg(shp.data?.images?.[0]?.url || null);
      setMuhasebe(mu.data || null);
      const rows = dpc.data?.rows || [];
      const dmap = {};
      rows.forEach((r) => { dmap[r.cabin_id] = r.paid_try || 0; });
      setDigerPerCabinPaid(dmap);
      setDigerSummary({ target_try: dsum.data?.target_try || 0, paid_try: dsum.data?.paid_try || 0 });
      // Alışveriş ödemeleri kabin bazında (from payments where type=alisveris)
      const paymentsArr = Array.isArray(p.data) ? p.data : [];
      const actualsArr = Array.isArray(ac.data) ? ac.data : [];
      const smap = {};
      let shopTotal = 0;
      // 1) payments type=alisveris (kabin_id bazlı)
      paymentsArr.filter((x) => x.payment_type === "alisveris").forEach((x) => {
        const cid = x.cabin_id || "";
        smap[cid] = (smap[cid] || 0) + (x.amount_try || 0);
        shopTotal += (x.amount_try || 0);
      });
      // 2) actuals (payer_username → cabin lookup)
      const userToCabin = {};
      (Array.isArray(c.data) ? c.data : []).forEach((cb) => {
        (cb.occupants || []).forEach((u) => { userToCabin[u] = cb.id; });
      });
      actualsArr.forEach((a) => {
        const cid = userToCabin[a.payer_username] || "";
        smap[cid] = (smap[cid] || 0) + (a.amount_try || 0);
        shopTotal += (a.amount_try || 0);
      });
      setShopPerCabinPaid(smap);
      setShopPaidTotal(shopTotal);
    } catch (e) {
      console.error("Dashboard load failed:", e);
    }
  };
  useEffect(() => { load(); }, []);

  const refreshRate = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/settings/refresh-tcmb");
      setSettings(data);
    } catch (e) {
      alert(e.response?.data?.detail || "TCMB kuru alınamadı");
    } finally {
      setRefreshing(false);
    }
  };

  const paidEurByCabin = useMemo(() => {
    const m = {};
    for (const p of payments) {
      // Sadece Tekne (gemi) kirası ödemeleri kabin €'suna dahil
      if ((p.payment_type || "gemi") !== "gemi") continue;
      m[p.cabin_id] = (m[p.cabin_id] || 0) + (p.amount_eur || 0);
    }
    return m;
  }, [payments]);

  const safeCabins = Array.isArray(cabins) ? cabins : [];
  const safeShopping = Array.isArray(shopping) ? shopping : [];
  const visibleCabins = safeCabins.filter((c) => !c.empty);
  const totalPersons = visibleCabins.reduce((s, c) => s + (c.people?.length || 0), 0);
  // Menü & Erzak Genel Liste tahmini toplam (excluded_from_cost dışlanır, itemTotal ile)
  const menuErzakTotal = safeShopping.reduce((s, i) => s + itemTotal(i), 0);
  const digerTargetTry = digerSummary.target_try || 0;
  const digerPaidTry = digerSummary.paid_try || 0;
  // Panel Diğer Bedel hesap — Muhasebe PDF ile aynı formül:
  //   toplam = paid + estimate × (1 - pct/100)
  // pct=100 → tahmini kısım tamamen gerçekleşti, sadece paid gösterilir
  // pct=0   → hiç gerçekleşmedi, tahmininin tamamı + paid gösterilir
  const shopFactor = (100 - Math.max(0, Math.min(100, (realizationShopPct || 0)))) / 100;
  const digerFactor = (100 - Math.max(0, Math.min(100, (realizationDigerPct || 0)))) / 100;
  // Panel top Diğer Bedel = SUM(her kabinin totalT) / SUM(her kabinin remT)
  const perCabinTry = useMemo(() => {
    return visibleCabins.map((c) => {
      const persons = c.people?.length || 0;
      const perPersonShopEst = totalPersons > 0 ? (menuErzakTotal / totalPersons) * shopFactor * persons : 0;
      const perPersonDigerEst = totalPersons > 0 ? (digerTargetTry / totalPersons) * digerFactor * persons : 0;
      const perPersonShopPaid = totalPersons > 0 ? (shopPaidTotal / totalPersons) * persons : 0;
      const perPersonDigerPaid = totalPersons > 0 ? (digerPaidTry / totalPersons) * persons : 0;
      const rawTotal = perPersonShopEst + perPersonDigerEst + (c.offset_try || 0) + perPersonDigerPaid + perPersonShopPaid;
      // Kabinin kendi offset kredisi payını aşıyorsa Toplam 0'a clamp'lenir
      // (matematik: rawTotal negatif → kredi > pay). Chip'te offset zaten görünüyor.
      const totalT = Math.max(0, rawTotal);
      const paidT = (shopPerCabinPaid[c.id] || 0) + (digerPerCabinPaid[c.id] || 0);
      return { id: c.id, totalT, paidT, remT: Math.max(0, totalT - paidT) };
    });
  }, [visibleCabins, menuErzakTotal, shopFactor, digerTargetTry, digerFactor, shopPaidTotal, digerPaidTry, totalPersons, shopPerCabinPaid, digerPerCabinPaid]);
  const otherTotalTry = perCabinTry.reduce((s, x) => s + x.totalT, 0);
  const otherPaidTry = perCabinTry.reduce((s, x) => s + x.paidT, 0);

  // Kim Kime Ne Ödemeli — Section 4 ile aynı hesap. Kabin ID bazında outgoing transferler.
  const settlementTransfers = useMemo(() => computeFullSettlement({
    cabins: visibleCabins,
    payments,
    actuals,
    shoppingItems: safeShopping,
    settings,
    digerSummary,
    itemTotal,
  }).transfers, [visibleCabins, payments, actuals, safeShopping, settings, digerSummary]);
  const transfersByFromCabin = useMemo(() => {
    const m = {};
    (settlementTransfers || []).forEach((t) => {
      if (!m[t.from_cabin_id]) m[t.from_cabin_id] = [];
      m[t.from_cabin_id].push(t);
    });
    return m;
  }, [settlementTransfers]);
  const otherRemainingTry = perCabinTry.reduce((s, x) => s + x.remT, 0);

  const totalEur = muhasebe?.cabin_total_eur ?? visibleCabins.reduce((s, c) => s + (c.total_eur || 0), 0);
  const paidEur = muhasebe?.collected_eur ?? Object.values(paidEurByCabin).reduce((a, b) => a + b, 0);
  const remainingEur = muhasebe?.remaining_eur ?? (totalEur - paidEur);

  const dateFmt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    return d.toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-5">
      {/* Hero summary — Tekne (€) + Diğer (₺) unified */}
      <section className="tk-card p-5 tk-rise" data-testid="summary-card">
        {/* Row 1 · TEKNE (€) */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>
              Tekne Bedeli
            </div>
            <div className="mt-1 text-3xl font-display font-semibold" data-testid="summary-total-eur">
              {fmtEUR(totalEur)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Kalan</div>
            <div className="text-2xl font-display font-semibold" style={{ color: "hsl(var(--primary))" }} data-testid="summary-remaining-eur">
              {fmtEUR(remainingEur)}
            </div>
          </div>
        </div>
        <div className="mt-3 h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
          <div className="h-full rounded-full" style={{ width: `${totalEur ? Math.min(100, (paidEur / totalEur) * 100) : 0}%`, backgroundColor: "hsl(var(--success))" }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-sm" style={{ color: "hsl(var(--text-2))" }}>
          <span className="inline-flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4" /> Toplanan: <b data-testid="summary-paid-eur" className="text-[hsl(var(--text-1))] font-semibold">{fmtEUR(paidEur)}</b>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Users className="w-4 h-4" /> {visibleCabins.length} kabin · {totalPersons} yolcu
          </span>
        </div>

        {/* Divider */}
        <div className="my-4 tk-divider" />

        {/* Row 2 · DİĞER (₺) — Alışveriş + Tekne Diğer Maliyet */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>
              Diğer Bedel
              {realizationShopPct !== 100 ? ` · Alışveriş %${realizationShopPct}` : ""}
              {realizationDigerPct !== 100 ? ` · Diğer %${realizationDigerPct}` : ""}
            </div>
            <div className="mt-1 text-3xl font-display font-semibold" data-testid="shopping-summary-estimated">
              {fmtTRY(otherTotalTry)}
            </div>
            <div className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>Alışveriş + Tekne Diğer Maliyet (tahmini + gerçekleşen)</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>Kalan</div>
            <div className="text-2xl font-display font-semibold" style={{ color: "hsl(var(--primary))" }} data-testid="shopping-summary-remaining">
              {fmtTRY(otherRemainingTry)}
            </div>
            <div className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>Toplam − Fiili ödenmiş</div>
          </div>
        </div>
        {otherTotalTry > 0 && (
          <>
            <div className="mt-3 h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
              <div className="h-full rounded-full" style={{ width: `${Math.min(100, (otherPaidTry / otherTotalTry) * 100)}%`, backgroundColor: "hsl(var(--warning))" }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-sm" style={{ color: "hsl(var(--text-2))" }}>
              <span>Ödenen: <b className="text-[hsl(var(--text-1))]">{fmtTRY(otherPaidTry)}</b></span>
              <span>Kişi başı: <b className="text-[hsl(var(--text-1))]" data-testid="summary-per-person-try">{totalPersons > 0 ? fmtTRY(otherTotalTry / totalPersons) : "—"}</b></span>
            </div>
          </>
        )}
      </section>

      {/* Tekne (full width) */}
      <Link
        to="/gemi"
        data-testid="gemi-btn"
        className="tk-card p-0 overflow-hidden relative block"
        style={{ minHeight: 140 }}
      >
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `linear-gradient(90deg, rgba(11,19,43,0.78) 0%, rgba(11,19,43,0.30) 100%), url('${shipImg ? `${process.env.REACT_APP_BACKEND_URL}${shipImg}` : "https://images.pexels.com/photos/4170111/pexels-photo-4170111.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}')` }} />
        <div className="relative p-5 text-white flex items-center gap-3 min-h-[140px]">
          <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
            <Anchor className="w-6 h-6" />
          </div>
          <div>
            <div className="font-display font-semibold text-xl">Tekne</div>
            <div className="text-sm text-white/85">Aria · bilgiler, görseller, sözleşme →</div>
          </div>
        </div>
      </Link>

      {/* Rate + Hava + Yolcu Listesi (3 cols) */}
      <section className="flex md:grid md:grid-cols-3 gap-3 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none -mx-3 px-3 md:mx-0 md:px-0 pb-1 md:pb-0">
        <div className="tk-card p-4 snap-start shrink-0 w-[70vw] md:w-auto" data-testid="rate-card">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-[10px] md:text-xs uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>
              TCMB € → ₺
            </div>
            <button data-testid="rate-refresh-btn" onClick={refreshRate} disabled={refreshing} className="tk-btn-ghost !p-1.5 disabled:opacity-60" title="TCMB'den yenile">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
          <div className="text-lg md:text-2xl font-display font-semibold" data-testid="rate-value">
            {Number(settings.eur_try_rate || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ₺
          </div>
          {settings.rate_updated_at && (
            <div className="text-[10px] mt-0.5 truncate" style={{ color: "hsl(var(--text-2))" }} title={dateFmt(settings.rate_updated_at)}>
              {dateFmt(settings.rate_updated_at)}
            </div>
          )}
        </div>

        <div className="snap-start shrink-0 w-[70vw] md:w-auto"><WeatherCard /></div>

        <button
          data-testid="passengers-btn"
          onClick={() => setShowPax(true)}
          className="tk-card p-4 text-left hover:bg-[hsl(var(--surface-2))] transition flex flex-col snap-start shrink-0 w-[70vw] md:w-auto"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] md:text-xs uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>
              Yolcu Listesi
            </div>
            <ListChecks className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
          </div>
          <div className="text-lg md:text-2xl font-display font-semibold">PDF indir</div>
          <div className="text-[10px] mt-0.5" style={{ color: "hsl(var(--text-2))" }}>{totalPersons} yolcu · {visibleCabins.length} kabin</div>
        </button>
      </section>

      {/* Cabin cards — mobil'de snap-scroll (yatay swipeable navigator), md+ grid */}
      <section className="space-y-3">
        <h2 className="text-lg font-display font-semibold px-1">Kabinlere Göre Ödemeler</h2>
        <div className="flex md:grid md:grid-cols-2 gap-3 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none -mx-3 px-3 md:mx-0 md:px-0 pb-2 md:pb-0" style={{ scrollbarWidth: "thin" }}>
          {visibleCabins.map((c, idx) => {
            const paidE = paidEurByCabin[c.id] || 0;
            const remE = (c.total_eur || 0) - paidE;
            const t = perCabinTry.find((x) => x.id === c.id) || { totalT: 0, paidT: 0, remT: 0 };
            const totalT = t.totalT, paidT = t.paidT, remT = t.remT;
            return (
              <div
                key={c.id}
                data-testid={`cabin-card-${c.id}`}
                className="tk-card p-5 tk-rise snap-start shrink-0 w-[88vw] md:w-auto"
                style={{ animationDelay: `${idx * 40}ms` }}
              >
                <div className="flex items-center gap-2 flex-nowrap overflow-x-auto no-scrollbar">
                  {/* Tek satır: KABİN N + avatarlar + isimler (aynı satırda, kompakt, sarma yok) */}
                  <span className="text-[11px] uppercase tracking-widest font-semibold shrink-0" style={{ color: "hsl(var(--text-2))" }}>
                    Kabin {c.order}
                  </span>
                  <div className="flex items-center gap-2 sm:gap-3 flex-nowrap">
                    {(c.people || []).map((p, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => navigate(`/kabinler#cabin-${c.id}`)}
                        data-testid={`dash-passenger-${c.id}-${i}`}
                        className="flex items-center gap-1.5 sm:gap-2 hover:opacity-80 transition-opacity shrink-0"
                        title={`${p.full_name} — kabine git`}
                      >
                        {p.avatar_url ? (
                          <img
                            src={p.avatar_url}
                            alt={p.full_name}
                            className="w-6 h-6 sm:w-7 sm:h-7 rounded-full object-cover flex-shrink-0"
                            style={{ backgroundColor: "hsl(var(--surface-2))" }}
                          />
                        ) : (
                          <span
                            className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-semibold"
                            style={{ backgroundColor: "hsl(var(--surface-2))", color: "hsl(var(--text-2))" }}
                          >
                            {(p.full_name || "?").trim().charAt(0).toLocaleUpperCase("tr-TR")}
                          </span>
                        )}
                        <span className="font-display font-semibold text-sm sm:text-lg whitespace-nowrap">{p.full_name || "—"}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* EUR row */}
                <div className="mt-4 rounded-xl px-4 py-3" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
                  <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "hsl(var(--text-2))" }}>EUR · Tekne Bedeli</div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <Stat label="Toplam" value={fmtEUR(c.total_eur)} testid={`cabin-${c.id}-eur-total`} />
                    <Stat label="Ödenen" value={fmtEUR(paidE)} color="hsl(var(--success))" testid={`cabin-${c.id}-eur-paid`} />
                    <Stat label="Kalan" value={fmtEUR(Math.max(0, remE))} color="hsl(var(--primary))" testid={`cabin-${c.id}-eur-remaining`} />
                  </div>
                </div>

                {/* TRY row */}
                <div className="mt-2 rounded-xl px-4 py-3" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
                  <div className="text-[10px] uppercase tracking-widest font-bold mb-1 flex items-center justify-between" style={{ color: "hsl(var(--text-2))" }}>
                    <span>TRY · Diğer Maliyet Payı{realizationShopPct !== 100 || realizationDigerPct !== 100 ? ` · Alış%${realizationShopPct} · Diğ%${realizationDigerPct}` : ""}</span>
                    {(c.offset_try ?? 0) !== 0 && (
                      <span className="tk-chip !py-0 !px-2" title="Sabit düzeltme">{(c.offset_try > 0 ? "+" : "") + fmtTRY(c.offset_try)}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <Stat label="Toplam" value={fmtTRY(totalT)} testid={`cabin-${c.id}-try-total`} />
                    <Stat label="Ödenen" value={fmtTRY(paidT)} color="hsl(var(--success))" testid={`cabin-${c.id}-try-paid`} />
                    <Stat label="Kalan" value={fmtTRY(Math.max(0, remT))} color="hsl(var(--primary))" testid={`cabin-${c.id}-try-remaining`} />
                  </div>
                </div>

                {/* HESAP DENGELEME — her kabinde göster (tasarım tutarlılığı için).
                    Transfer varsa Section 4'ten türetilen outgoing satırlar; yoksa sadece başlık + not. */}
                <div className="mt-2 rounded-xl px-4 py-3" style={{ backgroundColor: "#F3F4F6" }} data-testid={`cabin-${c.id}-ortak-hesap-dengeleme`}>
                  <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#4B5563" }}>HESAP DENGELEME</div>
                    <div className="text-[10px] italic" style={{ color: "#6B7280" }}>Not: Varsa aşağıdaki tutar(lar), yukarıdaki hesaba dahildir.</div>
                  </div>
                  {(transfersByFromCabin[c.id] || []).length > 0 && (
                    <div className="space-y-1">
                      {(transfersByFromCabin[c.id] || []).map((t, ti) => (
                        <div
                          key={ti}
                          data-testid={`cabin-${c.id}-transfer-${ti}`}
                          className="flex items-center gap-2 text-sm px-2.5 py-1.5 rounded"
                          style={{ backgroundColor: "#F3F4F6", color: "#111827" }}
                        >
                          <span className="font-medium">{t.from_name}</span>
                          <span aria-hidden style={{ color: "#6B7280" }}>→</span>
                          <span className="font-medium">{t.to_name}</span>
                          <span className="ml-auto font-semibold" style={{ color: "#111827" }}>{fmtTRY(t.amount_try)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {showPax && <PassengersModal cabins={visibleCabins} onClose={() => setShowPax(false)} />}
    </div>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div>
      <div className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>{label}</div>
      <div className="font-semibold" style={color ? { color } : undefined} data-testid={testid}>{value}</div>
    </div>
  );
}
